import get from 'lodash.get';
import logger from '~/services/logger';
import BrightcovePlayer from '~/services/plugins/brightcove-player/components/index.client.tsx';
import VideojsPlayer from '~/components/player/index.client.tsx';
import youboraAnalyticsManifest from '~/services/plugins/YouboraAnalytics/config/manifest.ts';
import jwAnalyticsManifest from '~/services/plugins/jw_analytics/config/manifest.ts';
import muxAnalyticsManifest from '~/services/plugins/mux-analytics/config/manifest.ts';
import brazeAnalyticsManifest from '~/services/plugins/zapp-analytics-plugin-braze/config/manifest.ts';
import brightcoveAnalyticsManifest from '~/services/plugins/brightcove-analytics/config/manifest.ts';
import defaultPlayerManifest from '~/services/plugins/QuickBrickPlayerPlugin/config/manifest.ts';
import brightcovePlayerManifest from '~/services/plugins/brightcove-player/config/manifest.ts';
import { getPluginConfig } from '~/utils/get-plugin-configuration.ts';
import type { Feed, FeedEntry } from '~/services/layout/index.ts';
import type { GetContextKeys } from '~/services/pipes/get-context-keys.ts';
import type { PlayerScreenType, Screen, ScreenType } from '~/services/layout';
import { isEmptyArray, isInvalidIndex } from '~/services/layout/layout';

export function getYouboraAnalyticsPluginData(
  pluginConfigurations: any,
  contextKeys: GetContextKeys
) {
  const { customConfiguration: youboraAnalytics } = getPluginConfig<
    typeof youboraAnalyticsManifest
  >(youboraAnalyticsManifest, pluginConfigurations);

  const { account_code, user_identifier_token, cdn } = youboraAnalytics;

  const userId =
    get(contextKeys, user_identifier_token as keyof GetContextKeys) || '-1';

  return { account_code, userId, cdn };
}

export function getJWAnalyticsPluginData(
  pluginConfigurations: any,
  contextKeys: GetContextKeys
) {
  const { customConfiguration: jwAnalytics } = getPluginConfig<
    typeof jwAnalyticsManifest
  >(jwAnalyticsManifest, pluginConfigurations);

  // Add check for JW analytic plugin and pass data to player client
  const { jw_token, jw_user_identity_storage_key } = jwAnalytics;

  const jwUserId =
    get(contextKeys, jw_user_identity_storage_key as keyof GetContextKeys) ||
    '-1';

  return { jw_token, jwUserId };
}

export function getMuxAnalyticsPluginData(
  pluginConfigurations: any,
  contextKeys: GetContextKeys
) {
  const { customConfiguration: muxAnalytics } = getPluginConfig<
    typeof muxAnalyticsManifest
  >(muxAnalyticsManifest, pluginConfigurations);

  const muxUserId: string =
    get(
      contextKeys,
      muxAnalytics?.user_identity_storage_key as keyof GetContextKeys
    ) || '-1';

  return { muxUserId, muxAnalytics };
}

export function getBrazeAnalyticsPluginData(
  pluginConfigurations: any,
  contextKeys: GetContextKeys
) {
  const { customConfiguration: brazeAnalytics } = getPluginConfig<
    typeof brazeAnalyticsManifest
  >(brazeAnalyticsManifest, pluginConfigurations);

  const { user_identity_storage_key } = brazeAnalytics;

  const brazeUserId: string = get(
    contextKeys,
    user_identity_storage_key as keyof GetContextKeys
  );

  return { brazeUserId, brazeAnalytics };
}

export function getBrightcoveAnalyticsPluginData(
  pluginConfigurations: any,
  contextKeys: GetContextKeys
) {
  const { customConfiguration: brightcoveAnalytics } = getPluginConfig<
    typeof brightcoveAnalyticsManifest
  >(brightcoveAnalyticsManifest, pluginConfigurations);

  const {
    enabled,
    account_id: brightcove_account_id,
    user_identity_storage_key,
    player_name,
    enable_qoe,
    engagement_update_interval,
  } = brightcoveAnalytics;

  const brightcoveUserId: string =
    get(contextKeys, user_identity_storage_key as keyof GetContextKeys) || '-1';

  return {
    brightcove_account_id,
    brightcoveUserId,
    brightcoveAnalytics: {
      enabled,
      player_name,
      enable_qoe,
      engagement_update_interval,
    },
  };
}

type DefaultPlayerStyles = NonNullable<
  ReturnType<typeof getPluginConfig<typeof defaultPlayerManifest>>['styles']
>;

export function getPlayerPluginConfig({
  playerScreen,
  pluginConfigurations,
}: {
  playerScreen?: Screen;
  pluginConfigurations: any;
}): {
  styles: DefaultPlayerStyles;
  brightcovePlayerAccountId: string;
  brightcovePlayerPolicyKey: string;
  brightcovePlayerViewerIdKey: string;
} {
  const screenType = playerScreen?.type as PlayerScreenType;
  const isQuickBrickPlayerPlugin = screenType === 'QuickBrickPlayerPlugin';
  const isBrightcovePlayer = screenType === 'quick-brick-player-brightcove';

  if (!isQuickBrickPlayerPlugin && !isBrightcovePlayer) {
    logger.error('Player screen not found');
    return {
      styles: {} as DefaultPlayerStyles,
      brightcovePlayerAccountId: '',
      brightcovePlayerPolicyKey: '',
      brightcovePlayerViewerIdKey: '',
    };
  }

  if (isQuickBrickPlayerPlugin) {
    const quickBrickConfig = getPluginConfig<typeof defaultPlayerManifest>(
      defaultPlayerManifest,
      playerScreen
    );

    return {
      styles: quickBrickConfig?.styles,
      brightcovePlayerAccountId: '',
      brightcovePlayerPolicyKey: '',
      brightcovePlayerViewerIdKey: '',
    };
  }

  const { customConfiguration: brightcoveConfig } = getPluginConfig<
    typeof brightcovePlayerManifest
  >(brightcovePlayerManifest, pluginConfigurations);

  const { styles } = getPluginConfig<typeof brightcovePlayerManifest>(
    brightcovePlayerManifest,
    playerScreen
  );

  return {
    styles: styles as DefaultPlayerStyles,
    brightcovePlayerAccountId:
      (playerScreen?.general?.account_id as string) ||
      brightcoveConfig?.account_id ||
      '',

    brightcovePlayerPolicyKey:
      (playerScreen?.general?.player_policy_key as string) ||
      brightcoveConfig?.player_policy_key ||
      '',

    brightcovePlayerViewerIdKey:
      (playerScreen?.general?.viewer_id as string) ||
      brightcoveConfig?.viewer_id ||
      '',
  };
}

const playerTypeToComponent = {
  QuickBrickPlayerPlugin: VideojsPlayer,
  'quick-brick-player-brightcove': BrightcovePlayer,
};

export function getPlayerComponent(screenType?: ScreenType) {
  if (!screenType) {
    return VideojsPlayer;
  }

  const component = playerTypeToComponent[screenType as PlayerScreenType];

  if (!component) {
    return VideojsPlayer;
  }

  return component;
}

/**
 * Get a playable item (FeedEntry) from a feed by entry ID or entry index.
 * @param feed - The feed containing the entries to search for the playable item.
 * @param entryId - The ID of the entry to find in the feed.
 * @param entryIndex - The index of the entry to find in the feed, used as a fallback if the entry ID is not found.
 * @returns - The playable item (FeedEntry) found by entry ID or entry index, or undefined if no playable item is found.
 */
export function getPlayableItemByEntryIdOrIndex({
  feed,
  entryId,
  entryIndex,
}: {
  feed?: Feed;
  entryId?: string;
  entryIndex?: number | string;
}): FeedEntry | undefined {
  const feedEntries = feed?.entry;

  if (isEmptyArray(feedEntries)) return undefined;

  const playableItemByEntryId: FeedEntry | undefined = feedEntries?.find(
    (entry: FeedEntry) => entry?.id === entryId
  );

  if (playableItemByEntryId) return playableItemByEntryId;

  if (isInvalidIndex(entryIndex)) return undefined;

  const playableItemByEntryIndex: FeedEntry | undefined =
    feedEntries?.[Number(entryIndex)];

  return playableItemByEntryIndex;
}
